// Procedural surface bakes.
//
// THE RULE FROM THE BRIEF: albedo, roughness and normal are derived from the SAME function so they
// can never disagree.
//
// This is the anti-tautology discipline applied to materials. Author an albedo map and a roughness
// map separately and they drift apart: you get shiny dirt, a rust patch that is smooth, a puddle
// that is rough. Here every material is ONE field function
//
//     field(u, v) -> { r, g, b, rough, height, metal }
//
// sampled once per texel, and the three maps are three readings of that one sample. The rust IS the
// rough spot IS the raised spot because they are the same number. Normals come from finite
// differences on the same height field, so a bump can never point the wrong way relative to the
// shading that produced it.
//
// Zero external assets: no image files, nothing fetched. Every byte below is computed.

import * as THREE from 'three';
import { fbm2, valueNoise2, rngFor } from '../rng.js';

const SIZE = 512;

/**
 * Bake one field function into an albedo / roughness / normal set.
 *
 * `repeat` is how many metres one tile covers -- it is carried on the textures so the caller cannot
 * set a world scale that disagrees with the scale the field was authored at.
 */
export function bake(name, field, { size = SIZE, repeat = 1, metres = 2.0 } = {}) {
  const n = size * size;
  const albedo = new Uint8Array(n * 4);
  const rough = new Uint8Array(n * 4);
  const height = new Float32Array(n);
  const normal = new Uint8Array(n * 4);

  // Single pass over the field. Everything else is read off `height`.
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const i = y * size + x;
      const s = field(x / size, y / size, x, y);
      albedo[i * 4] = s.r; albedo[i * 4 + 1] = s.g; albedo[i * 4 + 2] = s.b; albedo[i * 4 + 3] = 255;
      // Three.js reads roughness from the GREEN channel and metalness from BLUE, so one texture
      // can carry both. Packing them together also guarantees they are sampled at the same texel.
      const rr = Math.max(0, Math.min(255, Math.round(s.rough * 255)));
      const mm = Math.max(0, Math.min(255, Math.round((s.metal || 0) * 255)));
      rough[i * 4] = 255; rough[i * 4 + 1] = rr; rough[i * 4 + 2] = mm; rough[i * 4 + 3] = 255;
      height[i] = s.height;
    }
  }

  // Normal from the SAME height field. Sobel, wrapping, so tiles stay seamless.
  const at = (x, y) => height[((y + size) % size) * size + ((x + size) % size)];
  const texelMetres = metres / size;
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const i = y * size + x;
      const dx = (at(x + 1, y - 1) + 2 * at(x + 1, y) + at(x + 1, y + 1))
        - (at(x - 1, y - 1) + 2 * at(x - 1, y) + at(x - 1, y + 1));
      const dy = (at(x - 1, y + 1) + 2 * at(x, y + 1) + at(x + 1, y + 1))
        - (at(x - 1, y - 1) + 2 * at(x, y - 1) + at(x + 1, y - 1));
      // Scale slope into world units so a 2 mm bump reads as 2 mm regardless of texture resolution.
      let nx = -dx / (8 * texelMetres), ny = -dy / (8 * texelMetres), nz = 1;
      const len = Math.hypot(nx, ny, nz);
      nx /= len; ny /= len; nz /= len;
      normal[i * 4] = Math.round((nx * 0.5 + 0.5) * 255);
      normal[i * 4 + 1] = Math.round((ny * 0.5 + 0.5) * 255);
      normal[i * 4 + 2] = Math.round((nz * 0.5 + 0.5) * 255);
      normal[i * 4 + 3] = 255;
    }
  }

  const mk = (data, srgb) => {
    const t = new THREE.DataTexture(data, size, size, THREE.RGBAFormat);
    t.wrapS = t.wrapT = THREE.RepeatWrapping;
    t.repeat.set(repeat, repeat);
    t.magFilter = THREE.LinearFilter;
    t.minFilter = THREE.LinearMipmapLinearFilter;
    t.generateMipmaps = true;
    t.anisotropy = 8;
    if (srgb) t.colorSpace = THREE.SRGBColorSpace;
    t.name = `${name}.${srgb ? 'albedo' : 'data'}`;
    t.needsUpdate = true;
    return t;
  };

  return { map: mk(albedo, true), rm: mk(rough, false), nm: mk(normal, false), metres };
}

// ---------------------------------------------------------------------------
// Field helpers
// ---------------------------------------------------------------------------
const clamp = (v, a = 0, b = 1) => (v < a ? a : v > b ? b : v);
const mix = (a, b, t) => a + (b - a) * t;
const smoothstep = (e0, e1, x) => { const t = clamp((x - e0) / (e1 - e0)); return t * t * (3 - 2 * t); };

/** Tileable fbm: blend four shifted copies so the field wraps at the tile edge. */
function tfbm(u, v, freq, oct = 4, seed = 0) {
  const a = fbm2(u * freq, v * freq, oct, 2.0, 0.5, 1000 + seed);
  const b = fbm2((u - 1) * freq, v * freq, oct, 2.0, 0.5, 1000 + seed);
  const c = fbm2(u * freq, (v - 1) * freq, oct, 2.0, 0.5, 1000 + seed);
  const d = fbm2((u - 1) * freq, (v - 1) * freq, oct, 2.0, 0.5, 1000 + seed);
  return mix(mix(a, b, u), mix(c, d, u), v);
}

/** Worley-ish cell field -- aggregate, cobbles, tile joints, flaking paint edges. */
function cells(u, v, n, seed = 0) {
  const r = rngFor(`cells.${n}.${seed}`);
  let best = 1e9, second = 1e9, id = 0;
  const gx = Math.floor(u * n), gy = Math.floor(v * n);
  for (let oy = -1; oy <= 1; oy++) {
    for (let ox = -1; ox <= 1; ox++) {
      const cx = (gx + ox + n) % n, cy = (gy + oy + n) % n;
      const jx = valueNoise2(cx * 7.1 + 0.5, cy * 3.7 + 0.5, 77 + seed);
      const jy = valueNoise2(cx * 3.3 + 1.5, cy * 9.1 + 2.5, 91 + seed);
      const px = (gx + ox + jx) / n, py = (gy + oy + jy) / n;
      let ddx = px - u, ddy = py - v;
      if (ddx > 0.5) ddx -= 1; if (ddx < -0.5) ddx += 1;
      if (ddy > 0.5) ddy -= 1; if (ddy < -0.5) ddy += 1;
      const d = Math.hypot(ddx, ddy);
      if (d < best) { second = best; best = d; id = ((cx * 31 + cy * 17) % 97) / 97; }
      else if (d < second) second = d;
    }
  }
  void r;
  // NORMALISED by the cell count. The first version returned raw UV distance, so with n=46 every
  // possible d was below 0.02 and a `smoothstep(0.10, 0.02, d)` threshold returned 1.0 for every
  // texel -- the aggregate layer was a constant that cost compute and contributed nothing. Same bug
  // in concrete's pores (n=62) and rust pitting (n=40). Returning d*n makes every threshold
  // scale-independent, so changing n can never re-break them.
  return { d: best * n, edge: (second - best) * n, id };
}

/** Streaks running DOWN a wall -- the single most recognisable weathering signature there is. */
function runoff(u, v, seed = 0) {
  // Narrow horizontally, long vertically: the aspect ratio is what makes it read as rain, not noise.
  const s = tfbm(u * 5.5, v * 0.42, 3, 3, 400 + seed);
  return clamp((s - 0.42) * 2.6);
}

// ---------------------------------------------------------------------------
// The material library. One field function each.
// ---------------------------------------------------------------------------
export const FIELDS = {
  /** Wet asphalt: aggregate, tyre-polished centre, patch seams, cracks holding water. */
  asphalt(u, v) {
    const agg = cells(u, v, 74, 1);
    const grain = tfbm(u, v, 26, 4, 2);
    const patch = tfbm(u, v, 2.2, 2, 3);
    const crack = clamp(1 - Math.abs(tfbm(u, v, 5.5, 3, 4) - 0.5) * 9);

    let base = 0.055 + grain * 0.045 + agg.id * 0.030;
    base = mix(base, base * 1.22, smoothstep(0.55, 0.85, patch));      // a lighter patch repair
    // Aggregate stones catch light; the binder between them does not.
    const stone = smoothstep(0.48, 0.14, agg.d);
    base += stone * 0.045;

    let rough = 0.94 - stone * 0.10;
    let h = grain * 0.0016 + stone * 0.0022 - crack * 0.004;

    // Water sitting in the low spots. Roughness collapses, albedo darkens -- this is what makes a
    // night street read as WET rather than as grey.
    // Anisotropic: stretched ~8:1 along the road axis, because water runs in ruts and gathers at
    // the gutter. Isotropic round puddles read as noise, not as a wet road.
    const wet = smoothstep(0.40, 0.70, tfbm(u * 0.16, v * 1.3, 2.4, 3, 9))
      * smoothstep(0.9, 0.25, agg.d - crack * 0.5);
    rough = mix(rough, 0.055, wet);
    base = mix(base, base * 0.52, wet);

    const c = Math.round(base * 255);
    return { r: c, g: c + 1, b: c + 3, rough, height: h, metal: 0 };
  },

  /** Poured concrete: formwork lines, air bubbles, tide line at the base, rain streaking. */
  concrete(u, v) {
    const grain = tfbm(u, v, 34, 5, 11);
    const blotch = tfbm(u, v, 3.4, 3, 12);
    const pores = cells(u, v, 104, 13);
    const pore = smoothstep(0.20, 0.04, pores.d);
    // Formwork board lines every ~0.6 m, plus tie-bolt holes.
    const board = smoothstep(0.985, 1.0, Math.abs(Math.sin(v * Math.PI * 3.0)));
    const streak = runoff(u, v, 14);

    let L = 0.235 + grain * 0.060 + blotch * 0.070;
    L -= pore * 0.10;
    L -= board * 0.06;
    L = mix(L, L * 0.74, streak * 0.75);                 // dirt washes darker
    L = mix(L, L * 0.80, smoothstep(0.86, 1.0, v));      // tide line at the base of the wall

    const rough = clamp(0.90 + pore * 0.06 - streak * 0.10);
    const h = grain * 0.0011 - pore * 0.0016 - board * 0.0022;
    const r = Math.round(L * 258), g = Math.round(L * 254), b = Math.round(L * 247);
    return { r, g, b, rough, height: h, metal: 0 };
  },

  /** Brick: courses with mortar joints, per-brick colour variation, spalling, efflorescence. */
  brick(u, v) {
    // 215 x 65 mm face on a 10 mm joint over a 2.0 m tile => 27 courses, 9 stretchers. The first
    // version used 16 x 8, i.e. 125 mm courses -- roughly 2x oversize, which a critic read as "a
    // model rather than a building". Brick course height is one of the few dimensions a viewer
    // knows without being able to say so.
    const rows = 27, cols = 9;
    const ry = v * rows, row = Math.floor(ry), fy = ry - row;
    const off = (row % 2) * 0.5;                          // stretcher bond
    const rx = u * cols + off, col = Math.floor(rx), fx = rx - col;
    const jx = 0.035, jy = 0.10;
    const joint = (fx < jx || fx > 1 - jx || fy < jy || fy > 1 - jy) ? 1 : 0;

    const id = ((col * 37 + row * 53) % 89) / 89;
    const grain = tfbm(u, v, 40, 4, 21);
    const spall = smoothstep(0.72, 0.95, valueNoise2(col * 5.1, row * 7.3, 55));

    let R, G, Bc, rough, h;
    if (joint) {
      const L = 0.30 + grain * 0.07;
      R = L * 250; G = L * 246; Bc = L * 236;
      rough = 0.95; h = -0.0034;   // recessed bed joint throws a shadow line under raking light
    } else {
      // Per-unit variance: a brick wall is a mosaic of a hundred slightly different colours, and
      // that variance IS its visual identity. Includes a few markedly darker and paler outliers.
      const outlier = ((col * 71 + row * 19) % 23) / 23;
      const dark = outlier > 0.90 ? 0.62 : outlier < 0.06 ? 1.32 : 1.0;
      const L = (0.19 + id * 0.15 + grain * 0.05) * dark;
      R = L * 244; G = L * 186; Bc = L * 164;
      rough = 0.80 + grain * 0.06 + id * 0.12;
      h = 0.0030 + grain * 0.0008 - spall * 0.004;
      if (spall > 0.5) { R *= 0.86; G *= 0.90; Bc *= 0.94; }
    }
    const streak = runoff(u, v, 22) * 0.6;
    R = mix(R, R * 0.72, streak); G = mix(G, G * 0.74, streak); Bc = mix(Bc, Bc * 0.78, streak);
    return { r: Math.round(R), g: Math.round(G), b: Math.round(Bc), rough, height: h, metal: 0 };
  },

  /**
   * Painted metal worn through to bare steel.
   *
   * The brief asks for "paint worn through to metal where hands and bodies have touched it for
   * years". One field does it: a wear mask drives albedo toward steel, roughness DOWN (polished by
   * contact) and metalness UP -- all three from the same number, which is why it looks like wear
   * and not like a dirt decal.
   */
  painted(u, v) {
    const grain = tfbm(u, v, 30, 4, 31);
    const chip = cells(u, v, 54, 32);
    const wearField = tfbm(u, v, 4.5, 3, 33);
    const wear = clamp(smoothstep(0.52, 0.88, wearField) + smoothstep(0.16, 0.03, chip.edge) * 0.22);
    const rust = clamp(smoothstep(0.62, 0.95, tfbm(u, v, 9.0, 4, 34)) * (0.35 + wear));

    // Paint
    let R = 0.17, G = 0.26, Bc = 0.30;
    R += grain * 0.05; G += grain * 0.05; Bc += grain * 0.05;
    // Bare steel underneath
    R = mix(R, 0.36, wear); G = mix(G, 0.37, wear); Bc = mix(Bc, 0.39, wear);
    // Rust on top of the exposed steel
    R = mix(R, 0.35, rust); G = mix(G, 0.16, rust); Bc = mix(Bc, 0.075, rust);

    const rough = clamp(mix(0.62, 0.34, wear) + rust * 0.45);
    const metal = clamp(mix(0.15, 0.85, wear) * (1 - rust * 0.7));
    const h = grain * 0.0006 - smoothstep(0.16, 0.03, chip.edge) * 0.0006 + rust * 0.0008;
    return { r: Math.round(R * 255), g: Math.round(G * 255), b: Math.round(Bc * 255), rough, height: h, metal };
  },

  /** Heavily rusted / unpainted steel: pitting, scale, flaking. */
  rusted(u, v) {
    const base = tfbm(u, v, 7.0, 5, 41);
    const pit = cells(u, v, 76, 42);
    const scale = smoothstep(0.45, 0.80, tfbm(u, v, 17, 4, 43));
    const deep = smoothstep(0.22, 0.04, pit.d);

    let R = mix(0.30, 0.44, base), G = mix(0.14, 0.22, base), Bc = mix(0.07, 0.10, base);
    R = mix(R, 0.22, scale); G = mix(G, 0.10, scale); Bc = mix(Bc, 0.055, scale);
    R = mix(R, 0.38, deep * 0.28);
    const rough = clamp(0.78 + scale * 0.16 + deep * 0.05);
    const metal = clamp(0.55 - scale * 0.35 - deep * 0.2);
    const h = base * 0.0022 - deep * 0.0016 + scale * 0.0011;
    return { r: Math.round(R * 255), g: Math.round(G * 255), b: Math.round(Bc * 255), rough, height: h, metal };
  },

  /** Clean-ish structural steel, lightly dirtied. */
  steel(u, v) {
    const grain = tfbm(u, v, 44, 3, 51);
    const dirt = clamp(smoothstep(0.55, 0.9, tfbm(u, v, 6.0, 3, 52)) + runoff(u, v, 53) * 0.4);
    const L = mix(0.34, 0.24, dirt) + grain * 0.045;
    const rough = clamp(mix(0.36, 0.72, dirt) + grain * 0.06);
    const metal = clamp(0.92 - dirt * 0.4);
    return {
      r: Math.round(L * 252), g: Math.round(L * 254), b: Math.round(L * 262),
      rough, height: grain * 0.0005, metal,
    };
  },

  /** Pavement slabs: laid, jointed, chipped at the corners, worn down the walking line. */
  pavement(u, v) {
    const nx = 4, ny = 4;
    const gx = u * nx, gy = v * ny;
    const cx = Math.floor(gx), cy = Math.floor(gy);
    const fx = gx - cx, fy = gy - cy;
    const j = 0.028;
    const joint = (fx < j || fx > 1 - j || fy < j || fy > 1 - j) ? 1 : 0;
    const id = ((cx * 41 + cy * 23) % 71) / 71;
    const grain = tfbm(u, v, 36, 4, 61);
    const chip = smoothstep(0.12, 0.0, Math.min(Math.hypot(fx, fy), Math.hypot(1 - fx, fy),
      Math.hypot(fx, 1 - fy), Math.hypot(1 - fx, 1 - fy))) * smoothstep(0.6, 0.9, id);
    // Wear polish down the middle of the tile run.
    const polish = smoothstep(0.55, 0.15, Math.abs(v - 0.5)) * 0.6;
    const vary = ((cx * 53 + cy * 97) % 61) / 61;   // per-slab tonal jitter

    // Per-slab tonal variance is the dominant visual signal of real paving; without it a pavement
    // reads as a chessboard of one value. +-12% per slab, plus a darker bleed either side of the
    // joint where dirt washes out.
    const bleed = smoothstep(0.16, 0.03, Math.min(fx, 1 - fx, fy, 1 - fy));
    let L = joint ? 0.105 + grain * 0.035
      : (0.175 + id * 0.040 + grain * 0.040) * (0.88 + vary * 0.24);
    L = mix(L, L * 0.80, bleed * 0.7);
    L -= chip * 0.05;
    const rough = clamp((joint ? 0.95 : 0.87 + grain * 0.05) - polish * 0.16);
    const h = joint ? -0.0022 : grain * 0.0009 - chip * 0.003;
    const c = L * 255;
    return { r: Math.round(c * 1.00), g: Math.round(c * 0.99), b: Math.round(c * 0.97), rough, height: h, metal: 0 };
  },

  /** Kerb stone: harder, greyer, chipped top arris, tyre scuffs. */
  kerb(u, v) {
    const grain = tfbm(u, v, 30, 4, 71);
    const scuff = smoothstep(0.6, 0.95, tfbm(u, v, 8, 3, 72));
    const L = 0.175 + grain * 0.050 - scuff * 0.035;
    return {
      r: Math.round(L * 252), g: Math.round(L * 252), b: Math.round(L * 250),
      rough: clamp(0.88 + grain * 0.06 - scuff * 0.12), height: grain * 0.0013, metal: 0,
    };
  },

  /** Open steel grating: bars with holes between them. Height carries the void. */
  grate(u, v) {
    const n = 12;
    const fx = (u * n) % 1, fy = (v * n * 0.25) % 1;
    const bar = (fx < 0.22 || fy < 0.42) ? 1 : 0;
    const grain = tfbm(u, v, 40, 3, 81);
    const L = bar ? 0.20 + grain * 0.05 : 0.035;
    return {
      r: Math.round(L * 250), g: Math.round(L * 252), b: Math.round(L * 254),
      rough: bar ? 0.55 + grain * 0.15 : 0.95,
      height: bar ? 0.003 : -0.006,
      metal: bar ? 0.85 : 0.1,
    };
  },

  /** Weathered plastic / painted panel casing: chalky, UV-faded, streaked. */
  plastic(u, v) {
    const grain = tfbm(u, v, 48, 3, 91);
    const fade = tfbm(u, v, 3.0, 2, 92);
    const streak = runoff(u, v, 93) * 0.5;
    let L = 0.20 + fade * 0.07 + grain * 0.035;
    L = mix(L, L * 0.78, streak);
    return {
      r: Math.round(L * 250), g: Math.round(L * 252), b: Math.round(L * 254),
      rough: clamp(0.60 + fade * 0.2 + streak * 0.12), height: grain * 0.0004, metal: 0.02,
    };
  },

  /** Arcade floor tile: small units, grout, a polished path worn down the centre. */
  tile(u, v) {
    const n = 10;
    const gx = u * n, gy = v * n;
    const cx = Math.floor(gx), cy = Math.floor(gy);
    const fx = gx - cx, fy = gy - cy;
    const j = 0.05;
    const joint = (fx < j || fx > 1 - j || fy < j || fy > 1 - j) ? 1 : 0;
    const id = ((cx * 29 + cy * 47) % 53) / 53;
    const grain = tfbm(u, v, 42, 3, 101);
    const path = smoothstep(0.42, 0.06, Math.abs(v - 0.5));
    let L = joint ? 0.115 : 0.205 + id * 0.045 + grain * 0.025;
    L = mix(L, L * 1.08, path * 0.5);
    return {
      r: Math.round(L * 254), g: Math.round(L * 246), b: Math.round(L * 232),
      rough: clamp((joint ? 0.92 : 0.40) - path * 0.14),
      height: joint ? -0.0035 : grain * 0.0004, metal: 0,
    };
  },
};

// ---------------------------------------------------------------------------
// World-space wear
// ---------------------------------------------------------------------------
//
// A baked texture cannot know where on the building it landed, so every mask in a tile is
// necessarily position-independent -- and a critic named exactly that: "the dirt is a
// position-independent noise field with no spatial logic whatsoever… what is on these surfaces is
// NOISE, not WEAR. Wear is a function of position, orientation and use. Noise is a function of
// nothing."
//
// The three biggest missing terms all need world position, so they go in the shader:
//
//   GRAVITY  — dirt streaks DOWNWARD from every ledge, sill and fixing. This is the single most
//              recognisable signature of a weathered building, and every stain was a closed blob.
//   CONTACT  — walls carry 0.3–0.6 m of darker, dirtier material at their base, plus a hard darker
//              line in the last 50 mm. Every wall met the ground at its mid-wall value.
//   TRAFFIC  — floors polish along the walking line: darker, and markedly lower roughness.
//
// Both albedo and roughness are driven from the SAME mask, keeping the brief's rule intact across
// the shader boundary: grime is darker AND rougher because it is one number read twice.

const WEAR_GLSL = /* glsl */`
uniform float uWetness;
uniform float uRainTime;
varying vec3 vWPos;
varying vec3 vWNrm;

float np_hash(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123); }
float np_noise(vec2 p){
  vec2 i = floor(p), f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  return mix(mix(np_hash(i), np_hash(i + vec2(1.0, 0.0)), f.x),
             mix(np_hash(i + vec2(0.0, 1.0)), np_hash(i + vec2(1.0, 1.0)), f.x), f.y);
}

// Returns [grime, polish, wet].
vec3 npWear(vec3 p, vec3 n){
  float vert = 1.0 - abs(n.y);                  // 1 on a wall, 0 on a floor
  float horiz = p.x + p.z;                      // works for walls facing either axis

  // GRAVITY. Narrow horizontally, long vertically -- the aspect ratio is what makes it read as
  // rain-washed dirt rather than as noise. Frayed by a second high-frequency octave.
  float s = np_noise(vec2(horiz * 2.6, p.y * 0.16));
  float fray = 0.55 + 0.45 * np_noise(vec2(horiz * 19.0, p.y * 1.7));
  float streak = smoothstep(0.54, 0.88, s) * fray;
  streak *= smoothstep(26.0, 2.0, p.y);         // strongest low down, gone by roof level

  // CONTACT. Splash zone plus a tight dark line where wall meets ground.
  float contact = 1.0 - smoothstep(0.05, 1.05, p.y);
  contact = max(contact * 0.75, (1.0 - smoothstep(0.0, 0.09, p.y)) * 0.95);

  float grime = clamp((streak * 0.62 + contact) * vert, 0.0, 1.0);

  // TRAFFIC. A polished lane along the walking line, on up-facing surfaces only.
  float up = smoothstep(0.55, 0.95, n.y);
  float lane = smoothstep(4.6, 0.6, abs(p.x) - 5.0) * up;     // pavements flank the roadway
  lane += smoothstep(3.2, 0.4, abs(p.z + 20.35)) * up * 0.9;  // the arcade concourse
  lane *= 0.5 + 0.5 * np_noise(vec2(p.x * 0.7, p.z * 0.7));

  // WETNESS. Up-facing surfaces hold water; the ground is the whole point of a rainy-night street.
  // A critic: "the ground is dry... a real wet paver under that lamp is nearly a mirror with a long
  // specular tail; here it is matte tan, identical to the same shot in daylight."
  //
  // Broken, not uniform: a low-frequency pool mask, forced high in the gutter line either side of
  // the carriageway crown and in the alley drainage channels, because that is where water actually
  // goes. Dry patches between pools are what stop it reading as a sheet of glass.
  float pool = smoothstep(0.38, 0.70, np_noise(vec2(p.x * 0.33, p.z * 0.33)));
  pool = max(pool, smoothstep(0.22, 0.0, np_noise(vec2(p.x * 1.7, p.z * 1.7)) * 0.35
                                        + abs(abs(p.x) - 4.72) * 0.55));   // kerb gutters
  pool = max(pool * 0.85, 0.34);                                            // everything is damp
  float wet = up * pool * uWetness;

  return vec3(grime, clamp(lane, 0.0, 1.0), clamp(wet, 0.0, 1.0));
}
`;

/**
 * Inject world-space wear into a MeshStandardMaterial. Call once per material at build time.
 * Left as a shader patch rather than a second texture so the wear follows world position exactly
 * and cannot be tiled, mirrored or repeated the way a baked mask would be.
 */
export function applyWorldWear(mat, wetness = 1.0) {
  mat.onBeforeCompile = (shader) => {
    shader.uniforms.uWetness = { value: wetness };
    shader.uniforms.uRainTime = { value: 0 };
    mat.userData.shader = shader;
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vWPos;\nvarying vec3 vWNrm;')
      .replace('#include <beginnormal_vertex>',
        '#include <beginnormal_vertex>\nvWNrm = normalize(mat3(modelMatrix) * objectNormal);')
      .replace('#include <begin_vertex>',
        '#include <begin_vertex>\nvWPos = (modelMatrix * vec4(transformed, 1.0)).xyz;');

    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\n' + WEAR_GLSL)
      .replace('#include <map_fragment>', `#include <map_fragment>
        vec3 npW = npWear(vWPos, normalize(vWNrm));
        // Grime darkens and desaturates toward a neutral brown-grey rather than just going dark.
        vec3 npGrimeCol = mix(diffuseColor.rgb, vec3(0.052, 0.047, 0.041), 0.72);
        diffuseColor.rgb = mix(diffuseColor.rgb, npGrimeCol, npW.x * 0.80);
        diffuseColor.rgb *= (1.0 - npW.y * 0.22);
        // Standing water darkens what is under it -- a wet paver is roughly half the albedo of a
        // dry one, which is why a wet street reads as dark WITH bright reflections rather than as
        // uniformly grey.
        diffuseColor.rgb *= mix(1.0, 0.46, npW.z);`)
      .replace('#include <roughnessmap_fragment>', `#include <roughnessmap_fragment>
        // Same mask, second reading: grime is rougher, a polished lane is smoother.
        roughnessFactor = mix(roughnessFactor, 0.94, npW.x * 0.55);
        // 0.24 made a walked-on pavement more specular than polished granite, and the joint
        // normals lit up against the environment map as bright cyan seams. A worn flag is
        // smoother than a new one, not mirror-like.
        roughnessFactor = mix(roughnessFactor, 0.58, npW.y * 0.60);
        // Water collapses roughness. This is the single change that makes the ground read as wet:
        // at 0.06 the surface returns the environment and every emitter above it.
        roughnessFactor = mix(roughnessFactor, 0.055, npW.z);
        roughnessFactor = clamp(roughnessFactor, 0.02, 1.0);`);
  };
  // Force a distinct program so the patched and unpatched variants never share a cache entry.
  mat.customProgramCacheKey = () => 'np-wear-v1';
  return mat;
}

/** Build every field into a texture set. Called once at boot. */
export function bakeAll() {
  const scales = {
    asphalt: 3.0, concrete: 2.6, brick: 2.0, painted: 1.4, rusted: 1.2, steel: 1.0,
    pavement: 2.4, kerb: 1.2, grate: 1.0, plastic: 0.9, tile: 2.0,
  };
  const out = {};
  for (const [name, fn] of Object.entries(FIELDS)) {
    out[name] = bake(name, fn, { metres: scales[name] || 2.0 });
  }
  return out;
}
